import Config from "../utils/Config";
import axios from "axios";

//* Helper function to handle API error responses
const handleApiError = (error, defaultMessage = "An error occurred") => {
  let errorMessage = defaultMessage;
  let validationErrors = null;

  if (error.response) {
    if (error.response.status === 422) {
      if (error.response.data?.errors) {
        validationErrors = error.response.data.errors;
        const errorFields = Object.keys(error.response.data.errors);
        if (errorFields.length === 1) {
          errorMessage = error.response.data.errors[errorFields[0]][0];
        } else {
          errorMessage = `${
            error.response.data.errors[errorFields[0]][0]
          } (and ${errorFields.length - 1} more error${
            errorFields.length > 2 ? "s" : ""
          })`;
        }
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = "Validation error. Please check your input.";
      }
    } else if (error.response.data?.data?.reason) {
      errorMessage = error.response.data.data.reason;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response.status === 409) {
      errorMessage = "User already exists with this email or phone number.";
    } else if (error.response.status === 500) {
      errorMessage = "Server error. Please try again later.";
    }
  } else if (error.request) {
    errorMessage = "Network error. Please check your connection.";
  } else {
    errorMessage = error.message || "An unexpected error occurred";
  }

  return {
    status: false,
    message: errorMessage,
    validationErrors: validationErrors,
    data: null,
  };
};

class ProviderAuthService {
  getGuestHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
      },
    };
  }

  getAuthHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("trp_tk")}`,
      },
    };
  }

  async register(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/register`,
        payload,
        this.getGuestHeaders()
      );
      return {
        status: true,
        message: response.data.data?.reason || "Registration successful!",
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "An error occurred during registration");
    }
  }

  async verifyAccount(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/verify/account`,
        payload,
        this.getGuestHeaders()
      );
      return {
        status: true,
        message: response.data.data?.reason || "Account verified successfully!",
        data: response.data,
      };
    } catch (error) {
      return handleApiError(
        error,
        "An error occurred during account verification"
      );
    }
  }

  async resendVerificationCode(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/resend/verificationCode`,
        payload,
        this.getGuestHeaders()
      );
      return {
        status: true,
        message:
          response.data.data?.reason ||
          "Verification code resent successfully!",
        data: response.data,
      };
    } catch (error) {
      return handleApiError(
        error,
        "An error occurred while resending verification code"
      );
    }
  }

  async login(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/login`,
        payload,
        this.getGuestHeaders()
      );
      
      // Check if the response indicates success
      const responseData = response.data?.data;
      const isSuccess = responseData?.message === "Action Successful" || 
                       (responseData?.status_code === 200 && 
                        responseData?.message !== "Action Unsuccessful");
      
      if (isSuccess) {
        return {
          status: true,
          message: responseData?.reason || "Login successful!",
          data: response.data,
        };
      } else {
        return {
          status: false,
          message: responseData?.reason || "Login failed",
          data: response.data,
        };
      }
    } catch (error) {
      return handleApiError(error, "An error occurred during login");
    }
  }

  async forgotPassword(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/forgot-password`,
        payload,
        this.getGuestHeaders()
      );
      return {
        status: true,
        message: response.data.data?.reason || "Reset code sent successfully!",
        data: response.data,
      };
    } catch (error) {
      return handleApiError(
        error,
        "An error occurred while sending reset code"
      );
    }
  }

  async resetPassword(payload) {
    try {
      const response = await axios.post(
        `${Config.apiUrl}/provider/reset-password`,
        payload,
        this.getGuestHeaders()
      );
      return {
        status: true,
        message: response.data.data?.reason || "Password reset successfully!",
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "An error occurred during password reset");
    }
  }
}

const providerAuthService = new ProviderAuthService();
export default providerAuthService;
